import { z } from 'zod';
import { GIFT_CARD_KINDS } from '../constants/gift-cards.js';
import { FULFILLMENT_METHODS, ORDER_STATUSES, PRODUCT_TYPES } from '../constants/order-states.js';
import { SALES_CHANNELS } from '../constants/sales-channels.js';
import { usPhoneSchema, uuidSchema } from './common.js';
import { addressSchema } from './customer.js';
import { giftCardRecipientSchema } from './gift-card.js';
import { repairDateSchema } from './repair.js';
/**
 * The customer-facing half of the catalogue and the checkout.
 *
 * Everything here is deliberately narrower than the staff schemas next door:
 * a shopper never sees cost, margin, exact unit counts, or which physical
 * handset they are being given. The API picks the device; the customer picks
 * the model.
 */
// ---------------------------------------------------------------- catalogue
export const shopProductSchema = z.object({
    id: uuidSchema,
    sku: z.string(),
    type: z.enum(PRODUCT_TYPES),
    name: z.string(),
    description: z.string().nullable(),
    priceCents: z.number().int(),
    /** Optional discounted price, shown crossed-out next to priceCents on the
     *  product's own card/PDP when active. Not set on a listing's "From $X"
     *  price — that's the cheapest variation, not this product's own. */
    salePriceCents: z.number().int().nullable(),
    category: z.string().nullable(),
    brand: z.string().nullable(),
    imageUrl: z.string().nullable(),
    attributes: z.record(z.string(), z.union([z.string(), z.number(), z.boolean()])),
    inStock: z.boolean(),
    /**
     * Coarse on purpose. "3 left" is useful; the exact shelf count is an
     * operational figure and publishing it tells a competitor the shop's
     * turnover. Null whenever the product cannot run out.
     */
    stockLevel: z.enum(['out', 'low', 'in']).nullable(),
    /**
     * The exact count, published only once it is already the warning: set
     * when stockLevel is 'low', where "Only 3 left" is what moves someone to
     * decide. Null while the shelf is healthy — the count is the shop's
     * business — and null for anything that cannot run out.
     */
    stockLeft: z.number().int().nonnegative().nullable(),
    /** Set on a variation: which listing it belongs to. Null everywhere else. */
    parentId: uuidSchema.nullable(),
    /** How many variations this listing has. 0 for a normal product or a
     *  variation itself — only a listing groups anything. */
    variationCount: z.number().int().nonnegative().default(0),
    /** The cheapest active variation's price, set only when variationCount > 0
     *  so the card can read "From $X" instead of a single price. */
    priceFromCents: z.number().int().nullable().default(null),
    /** The most expensive active variation's price, paired with priceFromCents
     *  so the card can read "$X–$Y" (or a single price when they agree). Null
     *  whenever priceFromCents is. */
    priceToCents: z.number().int().nullable().default(null),
    /** Set only on a gift card product: whether it is emailed or handed over. */
    giftCardKind: z.enum(GIFT_CARD_KINDS).nullable().default(null),
});
/** One option in a variation group, as much as the picker needs to render
 *  and act on — not the full product shape. */
export const shopVariationSchema = z.object({
    sku: z.string(),
    name: z.string(),
    priceCents: z.number().int(),
    attributes: z.record(z.string(), z.union([z.string(), z.number(), z.boolean()])),
    inStock: z.boolean(),
    stockLevel: z.enum(['out', 'low', 'in']).nullable(),
    /** True for an option that has options of its own (a sub-listing such as
     *  "Sunbeam Pro" under "Sunbeam Phone"): its tile links to its own page
     *  instead of being bought, and shows the range below rather than one price. */
    hasOptions: z.boolean().default(false),
    /** Cheapest / dearest sellable variation under a sub-listing; null on a
     *  plain option, where priceCents is the price. */
    priceFromCents: z.number().int().nullable().default(null),
    priceToCents: z.number().int().nullable().default(null),
});
/**
 * One product, looked at properly.
 *
 * Adds where it is standing right now — but only the counters a customer can
 * actually walk into. Which warehouse a delivery would leave from is the
 * shop's business, not the customer's: all they need to know is that it can
 * be sent.
 */
export const shopProductDetailSchema = shopProductSchema.extend({
    /** Extra pictures after `imageUrl`, in display order. Empty when there are none. */
    galleryImageUrls: z.array(z.string()).default([]),
    collectFrom: z.array(z.object({
        locationId: uuidSchema,
        name: z.string(),
        address: z.string().nullable(),
    })),
    /**
     * Every option in this product's group, current one included. Populated
     * on anything with variations (its children — a sub-listing's page lists
     * its own children) and on a variation without any (its siblings) — never
     * on a standalone product.
     */
    variations: z.array(shopVariationSchema).default([]),
    /** Set on a variation or sub-listing: the SKU of the listing above it, so
     *  the page can link back to it. Null on a top-level product. */
    parentSku: z.string().nullable().default(null),
});
export const shopCatalogQuerySchema = z.object({
    q: z.string().trim().max(200).optional(),
    category: z.string().trim().max(64).optional(),
    brand: z.string().trim().max(64).optional(),
    /** A compatible-device tag as listed in the facets; matched exactly, case-insensitively. */
    device: z.string().trim().min(1).max(64).optional(),
    type: z.enum(PRODUCT_TYPES).optional(),
    minCents: z.coerce.number().int().nonnegative().optional(),
    maxCents: z.coerce.number().int().nonnegative().optional(),
    inStockOnly: z
        .union([z.boolean(), z.enum(['true', 'false'])])
        .transform((v) => (typeof v === 'boolean' ? v : v === 'true'))
        .optional(),
    sort: z.enum(['name', 'priceAsc', 'priceDesc']).default('name'),
    page: z.coerce.number().int().min(1).default(1),
    pageSize: z.coerce.number().int().min(1).max(60).default(24),
    /** Who is asking: the website by default, the kiosk tablet passes `kiosk`.
     *  It only picks which products are shown — the checkout enforces it too. */
    channel: z.enum(SALES_CHANNELS).default('web'),
});
/** The filter rail: what values actually exist, with counts. */
export const shopFacetsSchema = z.object({
    /**
     * Already in the order the shop is walked, and carrying the line that goes
     * under each heading — both come from the managed category list, so the
     * storefront does not keep its own copy of either.
     */
    categories: z.array(z.object({
        value: z.string(),
        count: z.number().int(),
        description: z.string().nullable(),
    })),
    brands: z.array(z.object({ value: z.string(), count: z.number().int() })),
    /** Compatible-device tags in use, case variants collapsed into one entry. */
    devices: z.array(z.object({ value: z.string(), count: z.number().int() })).default([]),
    priceRange: z.object({ minCents: z.number().int(), maxCents: z.number().int() }),
});
/** Facets can be narrowed by the same search the catalogue accepts. */
export const shopFacetsQuerySchema = shopCatalogQuerySchema.pick({ q: true, channel: true });
// --------------------------------------------------------------------- cart
/**
 * The cart lives in the browser, not the database.
 *
 * A server cart would have to either reserve stock — locking the last handset
 * to someone who wandered off — or not reserve it, in which case it is just a
 * list of ids with extra steps. So the browser keeps the list, and this
 * endpoint re-prices and re-checks it. Price and availability are read from
 * the catalogue every time; the client's numbers are never trusted.
 */
export const cartLineInputSchema = z
    .object({
    productId: uuidSchema,
    qty: z.number().int().positive().max(99),
    /** Only meaningful for a plan line: the phone line the plan is for. */
    phoneLine: z.string().trim().max(30).nullable().optional(),
    /** Only meaningful for a digital gift card line: who it is emailed to. */
    giftCard: giftCardRecipientSchema.nullable().optional(),
})
    .refine((v) => !v.phoneLine || v.qty === 1, {
    message: 'A phone line names one line of service — quantity must be 1',
    path: ['qty'],
})
    .refine((v) => !v.giftCard || v.qty === 1, {
    message: 'A gift card goes to one recipient — add them to the basket one at a time',
    path: ['qty'],
});
export const cartInputSchema = z.object({
    lines: z.array(cartLineInputSchema).max(50),
    fulfillmentMethod: z.enum(FULFILLMENT_METHODS).default('pickup'),
    /**
     * Which counter the customer intends to collect from. Availability itself is
     * network-wide — anything the shop sells anywhere can be brought to whichever
     * counter is chosen — so this only shapes which counter's *options* are
     * described back. Null means "any of them", the right answer while still
     * browsing.
     */
    pickupLocationId: uuidSchema.nullable().default(null),
    /** A promo code the customer typed. Never an amount — the server decides
     *  what it is worth. */
    couponCode: z.string().trim().min(1).max(64).nullable().default(null),
    /** Gift card codes the customer typed. Never an amount — the server reads
     *  the balance and decides how much of it this basket can use. */
    giftCardCodes: z.array(z.string().trim().min(1).max(64)).max(3).default([]),
    /** Spend the signed-in customer's store credit on this basket. */
    applyStoreCredit: z.boolean().default(false),
});
export const pricedCartLineSchema = z.object({
    productId: uuidSchema,
    sku: z.string(),
    name: z.string(),
    type: z.enum(PRODUCT_TYPES),
    imageUrl: z.string().nullable(),
    unitPriceCents: z.number().int(),
    /** What the customer asked for. */
    requestedQty: z.number().int(),
    /** What can actually be supplied — lower when the shelf has run down. */
    qty: z.number().int(),
    lineTotalCents: z.number().int(),
    /** Tax owed on this line — 0 whenever the product is non-taxable or the
     *  customer is exempt. */
    taxCents: z.number().int(),
    /** Set when qty was reduced or the line cannot be supplied at all. */
    problem: z.string().nullable(),
});
export const pricedCartSchema = z.object({
    lines: z.array(pricedCartLineSchema),
    subtotalCents: z.number().int(),
    taxCents: z.number().int(),
    /** Whether a signed-in customer's tax-exempt status was applied here. */
    taxExemptApplied: z.boolean(),
    shippingCents: z.number().int(),
    totalCents: z.number().int(),
    fulfillmentMethod: z.enum(FULFILLMENT_METHODS),
    /** True when nothing was adjusted, so checkout can proceed unchanged. */
    isClean: z.boolean(),
    /**
     * Every counter that could be collected from, and what collecting there
     * would involve. A counter missing something is not refused any more — the
     * shop can bring it over — so an option is only unselectable when the item
     * is out of stock everywhere, not merely absent from that one shelf.
     */
    pickupOptions: z.array(z.object({
        locationId: uuidSchema,
        name: z.string(),
        address: z.string().nullable(),
        /** True when every line is already standing at this counter. */
        canSupplyAll: z.boolean(),
        /** Names of every item this counter does not have enough of, whatever the reason. */
        missing: z.array(z.string()),
        /** Short here but standing elsewhere — the shop will bring it to this counter. */
        needsTransfer: z.array(z.string()),
        /** Nowhere in stock right now; this option cannot be chosen while these are in the basket. */
        unavailable: z.array(z.string()),
    })),
    /** Spend needed to reach free shipping; null when already there or on pickup. */
    freeShippingRemainingCents: z.number().int().nullable(),
    /** Coupon discount already subtracted from `totalCents`; 0 when none. */
    discountCents: z.number().int(),
    /** The code that was applied, as stored; null when none or rejected. */
    coupon: z.object({ code: z.string(), discountCents: z.number().int() }).nullable(),
    /** Why a submitted code was NOT applied. Never fails the price call. */
    couponProblem: z.string().nullable(),
    /** Gift card balance applied, already counted in `creditAppliedCents`. */
    giftCardAppliedCents: z.number().int().default(0),
    /** Store credit applied. */
    storeCreditAppliedCents: z.number().int().default(0),
    /** The signed-in customer's whole balance; null when signed out. */
    storeCreditAvailableCents: z.number().int().nullable().default(null),
    /** What still has to be paid by card or at the counter: total minus credit. */
    dueCents: z.number().int().default(0),
    giftCards: z
        .array(z.object({
        codeLast4: z.string(),
        appliedCents: z.number().int(),
        balanceCents: z.number().int(),
    }))
        .default([]),
    /** Why a submitted gift card code or the store credit was NOT applied. */
    creditProblem: z.string().nullable().default(null),
});
// ----------------------------------------------------------------- checkout
export const checkoutSchema = z.object({
    lines: z.array(cartLineInputSchema).min(1).max(50),
    fulfillmentMethod: z.enum(FULFILLMENT_METHODS),
    /** Required for shipping; ignored for pickup. */
    shippingAddress: addressSchema.nullable().default(null),
    /**
     * Required for collection whenever the shop has more than one counter open
     * to the website; ignored for delivery. The order is booked to this
     * location; stock may be drawn from any selling location and brought there
     * for hand-over on an order-linked stock request.
     */
    pickupLocationId: uuidSchema.nullable().default(null),
    /** Saves the address onto the customer record for next time. */
    saveAddress: z.boolean().default(false),
    note: z.string().trim().max(1000).nullable().default(null),
    /**
     * `pay_in_store` leaves the order awaiting payment and is settled at the
     * counter. `card` is only accepted when Stripe is configured.
     */
    paymentMethod: z.enum(['pay_in_store', 'card']).default('pay_in_store'),
    /**
     * One date for every repair in the basket — the day the customer will
     * bring the device(s) in. Required whenever the basket has a service line;
     * ignored otherwise. A request, not a confirmed booking: checkout validates
     * it is present and not in the past, but staff can still change it
     * afterwards, and the actual pickup date is only set once the device has
     * been checked in.
     */
    repairDropOffDate: repairDateSchema.nullable().default(null),
    /**
     * Where the device will be handed over. Only consulted when the order
     * itself is shipped — a collection order already has a counter, and that
     * counter is where the repair is dropped off too.
     */
    repairDropOffLocationId: uuidSchema.nullable().default(null),
    /**
     * Client-generated, stable for one checkout attempt. A double-tapped
     * "Place order" on a slow connection must not buy two phones.
     */
    idempotencyKey: z.string().trim().min(8).max(100),
    /**
     * SMS opt-in (t1029, carrier 10DLC review). Tri-state on purpose: `true`
     * grants consent, `false` revokes it, and `null`/absent leaves whatever is
     * recorded untouched — so a caller that doesn't know about this field can
     * never silently revoke someone's consent.
     */
    smsOptIn: z.boolean().nullable().default(null),
    /** Only consulted when `smsOptIn` is true and the customer has no phone on
     *  file yet — saved onto the account alongside the consent. */
    phone: usPhoneSchema.nullable().default(null),
    /** Promo code to apply; refused outright if it is no longer valid. */
    couponCode: z.string().trim().min(1).max(64).nullable().default(null),
    /** Refused outright if a card has no balance left by the time of checkout. */
    giftCardCodes: z.array(z.string().trim().min(1).max(64)).max(3).default([]),
    applyStoreCredit: z.boolean().default(false),
});
export const checkoutResultSchema = z.object({
    orderId: uuidSchema,
    orderNumber: z.string(),
    status: z.enum(ORDER_STATUSES),
    totalCents: z.number().int(),
    /** Present only when a card payment is still owed; hand to Stripe.js. Null
     *  with status 'paid' means gift card / store credit covered everything. */
    clientSecret: z.string().nullable(),
    /** Gift card and store credit held against this order. */
    creditAppliedCents: z.number().int().default(0),
    /** What is left to pay after credit. */
    dueCents: z.number().int().default(0),
});
// ------------------------------------------------------------- my account
export const shopOrderLineSchema = z.object({
    productName: z.string(),
    sku: z.string(),
    qty: z.number().int(),
    unitPriceCents: z.number().int(),
    lineTotalCents: z.number().int(),
});
export const shopOrderSchema = z.object({
    id: uuidSchema,
    orderNumber: z.string(),
    status: z.enum(ORDER_STATUSES),
    fulfillmentMethod: z.enum(FULFILLMENT_METHODS),
    placedAt: z.iso.datetime(),
    updatedAt: z.iso.datetime(),
    subtotalCents: z.number().int(),
    taxCents: z.number().int(),
    /** Whether this order's tax-exempt discount was applied when it was placed. */
    taxExemptApplied: z.boolean(),
    shippingCents: z.number().int(),
    /** Promo-code discount already taken off `totalCents`. */
    discountCents: z.number().int(),
    couponCode: z.string().nullable(),
    totalCents: z.number().int(),
    balanceDueCents: z.number().int(),
    shippingAddress: addressSchema.nullable(),
    note: z.string().nullable(),
    items: z.array(shopOrderLineSchema),
    /** Customer-readable milestones, oldest first. Internal notes are stripped. */
    timeline: z.array(z.object({
        status: z.enum(ORDER_STATUSES),
        at: z.iso.datetime(),
    })),
    /** Carrier tracking (t0800) — present once a label has been bought or a
     *  tracking number hand-entered, whichever this order actually has.
     *  `trackingCarrier`/`trackingUrl` are only ever set together with a
     *  number that came from a purchased label; a hand-typed one carries a
     *  number with no carrier or link. */
    trackingNumber: z.string().nullable(),
    trackingCarrier: z.string().nullable(),
    trackingUrl: z.string().nullable(),
});
export const shopOrderListItemSchema = shopOrderSchema
    .omit({
    items: true,
    timeline: true,
    note: true,
    shippingAddress: true,
    trackingNumber: true,
    trackingCarrier: true,
    trackingUrl: true,
})
    .extend({
    itemCount: z.number().int(),
    summary: z.string(),
});
export const shopProfileSchema = z.object({
    id: uuidSchema,
    phone: z.string().nullable(),
    name: z.string(),
    email: z.string().nullable(),
    address: addressSchema.nullable(),
    /** When SMS consent was recorded at checkout; null if never given or since
     *  revoked (t1029). Lets checkout prefill the opt-in checkbox. */
    smsConsentAt: z.iso.datetime().nullable(),
});
export const updateShopProfileSchema = z.object({
    name: z.string().trim().min(1).max(150).optional(),
    email: z.email().max(254).nullable().optional(),
    address: addressSchema.nullable().optional(),
});
// ------------------------------------------------------------------ pricing
/** t1130: why ordering is closed right now and what to tell the customer. */
export const storeClosureSchema = z.object({
    reason: z.enum(['shabbos', 'yom_tov', 'manual']),
    closedUntil: z.iso.datetime().nullable(),
    message: z.string(),
});
/** What a kiosk or tablet heartbeat carries while ordering is closed. */
export const closedNoticeSchema = z.object({
    closedUntil: z.iso.datetime().nullable(),
    message: z.string(),
});
/** Echoed to the storefront so the cart can show the same rules the API applies.
 *  Tax is no longer a single rate (t0947) — it is computed per line from each
 *  item's tax category, so there is no one figure to echo here any more. */
export const shopSettingsSchema = z.object({
    shippingFlatCents: z.number().int(),
    freeShippingThresholdCents: z.number().int().nullable(),
    /** False when Stripe is unconfigured — the card option is then hidden. */
    cardPaymentEnabled: z.boolean(),
    /** Ships to Stripe.js. Null whenever cardPaymentEnabled is false. */
    stripePublishableKey: z.string().nullable(),
    /**
     * Every counter a web order can be collected from. Empty when the shop only
     * delivers, in which case the storefront hides collection altogether.
     */
    pickupLocations: z.array(z.object({
        id: uuidSchema,
        name: z.string(),
        address: z.string().nullable(),
    })),
    /** False when nowhere is set up to ship, so the delivery option is hidden. */
    deliveryAvailable: z.boolean(),
    /** t1130: set while ordering is closed (Shabbos, Yom Tov, or a staff
     *  override). Browsing and the cart carry on; checkout is refused. */
    storeClosure: storeClosureSchema.nullable(),
});
// ------------------------------------------------------- back-office settings
/**
 * Shop-wide settings a manager can change from the back office.
 *
 * Anything a customer has already been quoted stays out of here on purpose —
 * postage is stamped onto an order as it is placed, and a setting that could
 * rewrite it after the fact would be a setting that changes what someone was
 * charged. Tax rates (t0947) are configurable too, but they are a list — one
 * rate per tax category — not a scalar, so they live in their own managed
 * list (`taxCategorySchema`) rather than here, same reasoning as
 * `supportCallTargets` living outside `supportSettings`. Whatever rate was in
 * force at checkout is still stamped onto the order the same way postage is.
 */
/** 4–8 digits — short enough to type on the vending tablet's on-screen
 *  keypad, long enough that the per-device lockout (see
 *  `storeSettingsSchema.vendingAdminMaxAttempts`) is doing real work. */
export const vendingAdminCodeSchema = z.string().regex(/^\d{4,8}$/, 'Numbers only, 4–8 digits');
const writableStoreSettingsSchema = z.object({
    /**
     * At or below this many on the shelf, a listing says "only a few left"
     * instead of "in stock" (zero switches that warning off). Also the back
     * office's default reorder point for products that don't set their own.
     */
    lowStockThreshold: z.number().int().min(0).max(1000),
    /** Who a vending tablet signs in as when unlocked with the admin bypass
     *  code (t0611) — null until an admin picks someone. */
    vendingAdminActsAsStaffId: uuidSchema.nullable(),
    /** Wrong tries on one tablet before it locks out. */
    vendingAdminMaxAttempts: z.number().int().min(1).max(100),
    /** How long that lockout lasts. */
    vendingAdminLockoutMinutes: z.number().int().min(1).max(1440),
    /** t0660: when on, every vending tablet installs each published release on
     *  its own within about a minute of it appearing, no staff action needed. */
    tabletAutoUpdate: z.boolean(),
    /** t1073: when on, a tablet sends a screenshot of the checkout screen to the
     *  back office whenever checkout fails. */
    tabletErrorCaptureScreenshots: z.boolean(),
    /** t1073: when on, a tablet also takes a photo with its front camera when
     *  checkout fails. This photographs the customer — leave off unless the
     *  store has signage and a retention policy that cover it. */
    tabletErrorCapturePhotos: z.boolean(),
    /** t1130: close kiosks and web checkout from candle-lighting to motzei on
     *  Shabbos and Yom Tov (Lakewood times). */
    shabbosModeEnabled: z.boolean(),
    /** Minutes before sunset that the closure starts (default 18). */
    shabbosCandleLightingOffsetMinutes: z.number().int().min(0).max(120),
    /** Minutes after sunset that ordering reopens (default 72). */
    shabbosMotzeiOffsetMinutes: z.number().int().min(0).max(180),
    /** Staff override of the calendar. It lapses on its own at the next
     *  candle-lighting or motzei, whichever comes first; null clears it. */
    shabbosOverride: z.enum(['open', 'closed']).nullable(),
    /** t1262: what the card machine (Square) charges per sale, in basis points
     *  (260 = 2.6%) plus a fixed amount in cents. The cost of goods report
     *  subtracts it from every card-machine sale. */
    cardMachineFeeBps: z.number().int().min(0).max(2000),
    cardMachineFeeFixedCents: z.number().int().min(0).max(500),
    /** t1365: default hourly rate for repair labour, in cents (0 = none set). */
    repairLabourRateCents: z.number().int().min(0).max(100000),
    /** t1365: the service item labour is sold as; its tax category taxes it. */
    repairLabourProductId: uuidSchema.nullable(),
    /** t1402: the filter kinds offered when a phone moves through provisioning. */
    provisioningFilterKinds: z.array(z.string().trim().min(1).max(60)).max(50),
});
export const shabbosStatusSchema = z.object({
    closed: z.boolean(),
    /** Why ordering is closed; null while open. 'manual' is a forced closure
     *  on an ordinary day. */
    reason: z.enum(['shabbos', 'yom_tov', 'manual']).nullable(),
    closedUntil: z.iso.datetime().nullable(),
    nextCloseAt: z.iso.datetime().nullable(),
    overrideActive: z.boolean(),
    /** When the override in force lapses; null with no active override. */
    overrideExpiresAt: z.iso.datetime().nullable(),
});
export const storeSettingsSchema = writableStoreSettingsSchema.extend({
    /** Read-only: whether a bypass code is currently set. The code itself is
     *  never returned — set or clear it through `/settings/vending-admin-code`. */
    vendingAdminCodeSet: z.boolean(),
    /** Read-only: where Shabbos mode stands right now, for the Settings preview. */
    shabbosStatus: shabbosStatusSchema,
});
export const updateStoreSettingsSchema = writableStoreSettingsSchema.partial();
// ---------------------------------------------------------- account notices
/**
 * t1080: one message shown to a signed-in customer on every shop page — today
 * only "your saved card is expiring", but `kind` is an enum precisely so other
 * account messages can join it later without reshaping the response.
 *
 * Flat, with a nullable `card`, rather than a discriminated union: a new kind
 * that carries no card (or a different payload) is then additive for the web
 * client instead of a breaking change.
 */
export const accountNoticeSchema = z.object({
    /** Stable per underlying condition (`card-expiry:<card row id>`), so the web
     *  client can remember a dismissal for the rest of the browser session. */
    id: z.string(),
    kind: z.enum(['card_expired', 'card_expiring']),
    /** Maps one-to-one onto the storefront's `mk-notice--warn` / `--bad` tones. */
    severity: z.enum(['warn', 'bad']),
    message: z.string(),
    /** Storefront-relative link that fixes it; null for purely informational notices. */
    action: z.object({ label: z.string(), href: z.string() }).nullable(),
    card: z
        .object({
        id: uuidSchema,
        brand: z.string(),
        last4: z.string(),
        expMonth: z.number().int().min(1).max(12),
        expYear: z.number().int(),
    })
        .nullable(),
});
//# sourceMappingURL=shop.js.map